import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Settings2, X, ChevronLeft, ChevronRight, Upload, ImageIcon } from "lucide-react";
import storageService from "@/lib/storage";
import ExportDialog from "@/components/ExportDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { refundsApi, refundPricesApi, refundBranchesApi, refundBanksApi } from "@/lib/unifiedApi";
import { sendNotification } from "@/lib/messaging";
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  addMonths, 
  subMonths,
  parseISO,
  isSameMonth
} from "date-fns";

// Default options
const DEFAULT_PRICES = [100, 120, 150, 160, 200, 240];
const DEFAULT_BRANCHES = ["FYM", "Reenp", "MILAN2", "DUSIT", "Sonny", "Rusty", "HOUSE OF BE...", "FRINGE", "LAMOON"];
const DEFAULT_BANKS = ["Kbank", "Krungthai", "Krungsri", "Bangkok Bank", "TTB"];

const REFUND_EXPORT_COLUMNS = [
  { key: 'date',          label: 'Date' },
  { key: 'branch',        label: 'Branch' },
  { key: 'price',         label: 'Price (฿)' },
  { key: 'quantity',      label: 'Quantity' },
  { key: 'total',         label: 'Total (฿)', format: (_, row) => String((row.price || 0) * (row.quantity || 0)) },
  { key: 'status',        label: 'Status' },
  { key: 'accountName',   label: 'Account Name' },
  { key: 'accountNumber', label: 'Account Number' },
  { key: 'bank',          label: 'Bank' },
];

const RefundPage = () => {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [editingRefund, setEditingRefund] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Dynamic prices, branches, and banks from Firebase
  const [prices, setPrices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [banks, setBanks] = useState([]);
  const [newPrice, setNewPrice] = useState("");
  const [newBranch, setNewBranch] = useState("");
  const [newBank, setNewBank] = useState("");
  const [editingPrice, setEditingPrice] = useState(null);
  const [editingBranch, setEditingBranch] = useState(null);
  const [editingBank, setEditingBank] = useState(null);
  const [editPriceValue, setEditPriceValue] = useState("");
  const [editBranchValue, setEditBranchValue] = useState("");
  const [editBankValue, setEditBankValue] = useState("");

  const [formData, setFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    price: "",
    quantity: 1,
    branch: "",
    status: "Pending",
    accountNumber: "",
    accountName: "",
    bank: "",
    slip_image_url: "",
  });

  // Slip upload state
  const [slipFile, setSlipFile] = useState(null);
  const [slipPreview, setSlipPreview] = useState(null);
  const [uploadingSlip, setUploadingSlip] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [viewingSlip, setViewingSlip] = useState(null);

  const statuses = ["Pending", "Transfered"];

  // Export filters (month defaults to 'current' — matches the existing month navigator)
  const [exportFilters, setExportFilters] = useState({ status: 'all', branch: 'all', month: 'current' });
  const exportData = refunds
    .filter((refund) => {
      if (exportFilters.status !== 'all' && refund.status !== exportFilters.status) return false;
      if (exportFilters.branch !== 'all' && refund.branch !== exportFilters.branch) return false;
      if (exportFilters.month === 'current') {
        if (!refund.date) return false;
        try { if (!isSameMonth(parseISO(refund.date), currentMonth)) return false; } catch { return false; }
      }
      return true;
    })
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  // Real-time subscriptions for Firebase data
  useEffect(() => {
    // Subscribe to refunds
    const unsubscribeRefunds = refundsApi.subscribe((data) => {
      setRefunds(data);
      setLoading(false);
    });

    // Subscribe to prices
    const unsubscribePrices = refundPricesApi.subscribe((data) => {
      if (data.length === 0) {
        // Initialize with defaults if empty
        const defaultPrices = DEFAULT_PRICES.map((p, idx) => ({ id: `price-${idx}`, value: p, isDefault: true }));
        setPrices(defaultPrices);
      } else {
        setPrices(data);
      }
    });

    // Subscribe to branches
    const unsubscribeBranches = refundBranchesApi.subscribe((data) => {
      if (data.length === 0) {
        // Initialize with defaults if empty
        const defaultBranches = DEFAULT_BRANCHES.map((b, idx) => ({ id: `branch-${idx}`, name: b, isDefault: true }));
        setBranches(defaultBranches);
      } else {
        setBranches(data);
      }
    });

    // Subscribe to banks
    const unsubscribeBanks = refundBanksApi.subscribe((data) => {
      if (data.length === 0) {
        // Initialize with defaults if empty
        const defaultBanks = DEFAULT_BANKS.map((b, idx) => ({ id: `bank-${idx}`, name: b, isDefault: true }));
        setBanks(defaultBanks);
      } else {
        setBanks(data);
      }
    });

    // Timeout fallback for loading
    const timeout = setTimeout(() => setLoading(false), 3000);

    return () => {
      unsubscribeRefunds();
      unsubscribePrices();
      unsubscribeBranches();
      unsubscribeBanks();
      clearTimeout(timeout);
    };
  }, []);

  // Price management
  const handleAddPrice = async () => {
    if (!newPrice || isNaN(parseFloat(newPrice))) return;
    const priceValue = parseFloat(newPrice);
    if (prices.some(p => p.value === priceValue)) {
      toast.error("Price already exists");
      return;
    }
    try {
      await refundPricesApi.create({ value: priceValue, isDefault: false });
      setNewPrice("");
      toast.success("Price added");
    } catch (error) {
      console.error("Error adding price:", error);
      // Fallback to local state
      const newPriceObj = { id: `price-${Date.now()}`, value: priceValue, isDefault: false };
      setPrices(prev => [...prev, newPriceObj]);
      setNewPrice("");
      toast.success("Price added (local)");
    }
  };

  const handleUpdatePrice = async (id) => {
    if (!editPriceValue || isNaN(parseFloat(editPriceValue))) return;
    const newValue = parseFloat(editPriceValue);
    try {
      await refundPricesApi.update(id, { value: newValue });
      setEditingPrice(null);
      setEditPriceValue("");
      toast.success("Price updated");
    } catch (error) {
      console.error("Error updating price:", error);
      setPrices(prices.map(p => p.id === id ? { ...p, value: newValue } : p));
      setEditingPrice(null);
      setEditPriceValue("");
      toast.success("Price updated (local)");
    }
  };

  const handleDeletePrice = async (id) => {
    if (!window.confirm("Delete this price?")) return;
    try {
      await refundPricesApi.delete(id);
      toast.success("Price deleted");
    } catch (error) {
      console.error("Error deleting price:", error);
      setPrices(prices.filter(p => p.id !== id));
      toast.success("Price deleted (local)");
    }
  };

  // Branch management
  const handleAddBranch = async () => {
    if (!newBranch.trim()) return;
    if (branches.some(b => b.name.toLowerCase() === newBranch.trim().toLowerCase())) {
      toast.error("Branch already exists");
      return;
    }
    try {
      await refundBranchesApi.create({ name: newBranch.trim(), isDefault: false });
      setNewBranch("");
      toast.success("Branch added");
    } catch (error) {
      console.error("Error adding branch:", error);
      const newBranchObj = { id: `branch-${Date.now()}`, name: newBranch.trim(), isDefault: false };
      setBranches(prev => [...prev, newBranchObj]);
      setNewBranch("");
      toast.success("Branch added (local)");
    }
  };

  const handleUpdateBranch = async (id) => {
    if (!editBranchValue.trim()) return;
    try {
      await refundBranchesApi.update(id, { name: editBranchValue.trim() });
      setEditingBranch(null);
      setEditBranchValue("");
      toast.success("Branch updated");
    } catch (error) {
      console.error("Error updating branch:", error);
      setBranches(branches.map(b => b.id === id ? { ...b, name: editBranchValue.trim() } : b));
      setEditingBranch(null);
      setEditBranchValue("");
      toast.success("Branch updated (local)");
    }
  };

  const handleDeleteBranch = async (id) => {
    if (!window.confirm("Delete this branch?")) return;
    try {
      await refundBranchesApi.delete(id);
      toast.success("Branch deleted");
    } catch (error) {
      console.error("Error deleting branch:", error);
      setBranches(branches.filter(b => b.id !== id));
      toast.success("Branch deleted (local)");
    }
  };

  // Bank management
  const handleAddBank = async () => {
    if (!newBank.trim()) return;
    if (banks.some(b => b.name.toLowerCase() === newBank.trim().toLowerCase())) {
      toast.error("Bank already exists");
      return;
    }
    try {
      await refundBanksApi.create({ name: newBank.trim(), isDefault: false });
      setNewBank("");
      toast.success("Bank added");
    } catch (error) {
      console.error("Error adding bank:", error);
      const newBankObj = { id: `bank-${Date.now()}`, name: newBank.trim(), isDefault: false };
      setBanks(prev => [...prev, newBankObj]);
      setNewBank("");
      toast.success("Bank added (local)");
    }
  };

  const handleUpdateBank = async (id) => {
    if (!editBankValue.trim()) return;
    try {
      await refundBanksApi.update(id, { name: editBankValue.trim() });
      setEditingBank(null);
      setEditBankValue("");
      toast.success("Bank updated");
    } catch (error) {
      console.error("Error updating bank:", error);
      setBanks(banks.map(b => b.id === id ? { ...b, name: editBankValue.trim() } : b));
      setEditingBank(null);
      setEditBankValue("");
      toast.success("Bank updated (local)");
    }
  };

  const handleDeleteBank = async (id) => {
    if (!window.confirm("Delete this bank?")) return;
    try {
      await refundBanksApi.delete(id);
      toast.success("Bank deleted");
    } catch (error) {
      console.error("Error deleting bank:", error);
      setBanks(banks.filter(b => b.id !== id));
      toast.success("Bank deleted (local)");
    }
  };

  // Helper: แจ้ง admin ผ่าน LINE เมื่อสร้าง refund ใหม่ที่เป็น Pending
  const notifyRefundToLine = (docId, refundData) => {
    fetch(process.env.REACT_APP_LINE_NOTIFY_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: docId,
        amount: refundData.total ?? refundData.price,
        date: refundData.date,
        branch: refundData.branch,
        accountNumber: refundData.accountNumber,
        accountName: refundData.accountName,
        bank: refundData.bank,
      }),
    }).catch((err) => console.error("notify-refund failed:", err));
  };

  // Refund CRUD
  const handleSubmit = async (e) => {
    e.preventDefault();
    const total = parseFloat(formData.price) * parseInt(formData.quantity);

    // Upload slip image if a new file was selected
    let slip_image_url = formData.slip_image_url;
    if (slipFile) {
      setUploadingSlip(true);
      try {
        const res = await storageService.uploadFile(
          slipFile,
          `refund-slips/${formData.date}`,
          (p) => setUploadProgress(p)
        );
        slip_image_url = res.url;
      } catch (err) {
        console.error("Slip upload failed:", err);
        toast.error("อัพโหลดสลิปไม่สำเร็จ — บันทึกโดยไม่มีสลิป");
      } finally {
        setUploadingSlip(false);
        setUploadProgress(0);
      }
    }

    const refundData = {
      ...formData,
      slip_image_url,
      total,
      price: parseFloat(formData.price),
      quantity: parseInt(formData.quantity),
    };

    try {
      if (editingRefund) {
        await refundsApi.update(editingRefund.id, refundData);
        toast.success("Refund updated");
      } else {
        const result = await refundsApi.create(refundData);
        toast.success("Refund added");
        // แจ้ง admin ผ่าน LINE ถ้าสถานะ Pending
        if (refundData.status === "Pending") {
          const docId = result?.data?.id ?? `refund-${Date.now()}`;
          notifyRefundToLine(docId, refundData);
        }
      }
      resetForm();
    } catch (error) {
      console.error("Error saving refund:", error);
      // Fallback to local state
      if (editingRefund) {
        setRefunds(refunds.map(r => r.id === editingRefund.id ? { ...refundData, id: editingRefund.id } : r));
        toast.success("Refund updated (local)");
      } else {
        const localId = `refund-${Date.now()}`;
        const newRefund = { ...refundData, id: localId };
        setRefunds(prev => [...prev, newRefund]);
        toast.success("Refund added (local)");
        // ยังแจ้ง LINE แม้ Firebase จะ fail
        if (refundData.status === "Pending") {
          notifyRefundToLine(localId, refundData);
        }
      }
      resetForm();
    }
  };

  const handleEdit = (refund) => {
    setEditingRefund(refund);
    setFormData({
      date: refund.date,
      price: String(refund.price ?? ""),
      quantity: refund.quantity,
      branch: refund.branch,
      status: refund.status,
      accountNumber: refund.accountNumber || "",
      accountName: refund.accountName || "",
      bank: refund.bank || "",
      slip_image_url: refund.slip_image_url || "",
    });
    setSlipFile(null);
    setSlipPreview(null);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this refund?")) return;
    try {
      await refundsApi.delete(id);
      toast.success("Refund deleted");
    } catch (error) {
      console.error("Error deleting refund:", error);
      setRefunds(refunds.filter(r => r.id !== id));
      toast.success("Refund deleted (local)");
    }
  };

  const handleStatusChange = async (refund, newStatus) => {
    try {
      await refundsApi.update(refund.id, { status: newStatus });
      toast.success("Status updated");
      // 🔔 Push notification — refund status change
      sendNotification({
        type: 'refund_status',
        title: `Refund: ${newStatus}`,
        body: `${refund.accountName || refund.branch || ''} · ฿${Number(refund.total || 0).toLocaleString()}`,
        targetUserIds: 'all',
        data: { type: 'refund_status', url: '/refund' },
      });
    } catch (error) {
      console.error("Error updating status:", error);
      setRefunds(refunds.map(r => r.id === refund.id ? { ...r, status: newStatus } : r));
      toast.success("Status updated (local)");
    }
  };

  const resetForm = () => {
    setEditingRefund(null);
    setFormData({
      date: format(new Date(), "yyyy-MM-dd"),
      price: "",
      quantity: 1,
      branch: "",
      status: "Pending",
      accountNumber: "",
      accountName: "",
      bank: "",
      slip_image_url: "",
    });
    setSlipFile(null);
    setSlipPreview(null);
    setDialogOpen(false);
  };

  // Filter refunds by current month
  const monthlyRefunds = refunds.filter(r => {
    try {
      return isSameMonth(parseISO(r.date), currentMonth);
    } catch {
      return false;
    }
  }).sort((a, b) => {
    const dateA = parseISO(a.date);
    const dateB = parseISO(b.date);
    return dateA - dateB;
  });

  // Calculate summary by branch
  const branchSummary = monthlyRefunds.reduce((acc, refund) => {
    if (!acc[refund.branch]) {
      acc[refund.branch] = { total: 0, count: 0 };
    }
    acc[refund.branch].total += (Number(refund.total) || 0);
    acc[refund.branch].count += (Number(refund.quantity) || 0);
    return acc;
  }, {});

  const totalMonthlyRefund = monthlyRefunds.reduce((sum, r) => sum + (Number(r.total) || 0), 0);

  const previousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const getBranchColor = (branchName) => {
    const colors = {
      "Sonny": "bg-red-500 text-white",
      "Rusty": "bg-gray-700 text-white",
      "DUSIT": "bg-purple-200 text-purple-800",
      "MILAN2": "bg-gray-200 text-gray-800",
      "FYM": "bg-gray-100 text-gray-800",
      "Reenp": "bg-gray-100 text-gray-800",
      "HOUSE OF BE...": "bg-gray-200 text-gray-800",
      "FRINGE": "bg-gray-200 text-gray-800",
      "LAMOON": "bg-gray-200 text-gray-800",
    };
    return colors[branchName] || "bg-gray-200 text-gray-800";
  };

  if (loading) {
    return (
      <div className="page-container" data-testid="refund-loading">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#BFFF00] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="refund-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Refund</h1>
          <p className="text-gray-500 mt-0.5 text-sm">Track refund amounts by date and branch</p>
        </div>
        <div className="flex gap-2 items-center">
          <ExportDialog
            title="Export Refunds"
            filename="refunds"
            data={exportData}
            columns={REFUND_EXPORT_COLUMNS}
            recordLabel="refunds"
          >
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-gray-700 text-sm">Month</Label>
                <Select value={exportFilters.month} onValueChange={(v) => setExportFilters((f) => ({ ...f, month: v }))}>
                  <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="current">{format(currentMonth, "MMMM yyyy")} (current view)</SelectItem>
                    <SelectItem value="all">All months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-700 text-sm">Status</Label>
                <Select value={exportFilters.status} onValueChange={(v) => setExportFilters((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">All statuses</SelectItem>
                    {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-700 text-sm">Branch</Label>
                <Select value={exportFilters.branch} onValueChange={(v) => setExportFilters((f) => ({ ...f, branch: v }))}>
                  <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">All branches</SelectItem>
                    {branches.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </ExportDialog>

          {/* Settings Button */}
          <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="refund-settings-btn" className="border-gray-200">
                <Settings2 size={18} className="sm:mr-2" />
                <span className="hidden sm:inline">Settings</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 max-w-xl">
              <DialogHeader>
                <DialogTitle className="text-gray-900">Manage Settings</DialogTitle>
              </DialogHeader>
              <Tabs defaultValue="prices" className="mt-4">
                <TabsList className="grid w-full grid-cols-3 bg-gray-100">
                  <TabsTrigger value="prices" className="data-[state=active]:bg-white">Prices</TabsTrigger>
                  <TabsTrigger value="branches" className="data-[state=active]:bg-white">Branches</TabsTrigger>
                  <TabsTrigger value="banks" className="data-[state=active]:bg-white">Banks</TabsTrigger>
                </TabsList>
                
                {/* Prices Tab */}
                <TabsContent value="prices" className="space-y-4 mt-4">
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Add new price..."
                      value={newPrice}
                      onChange={(e) => setNewPrice(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddPrice()}
                      className="border-gray-200"
                      data-testid="new-price-input"
                    />
                    <Button onClick={handleAddPrice} className="bg-[#BFFF00] hover:bg-[#a6e600] text-black">
                      <Plus size={18} />
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {prices.sort((a, b) => a.value - b.value).map((price) => (
                      <div key={price.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        {!price.isDefault && editingPrice === price.id ? (
                          <>
                            <Input
                              type="number"
                              value={editPriceValue}
                              onChange={(e) => setEditPriceValue(e.target.value)}
                              className="flex-1 border-gray-200 h-8"
                              autoFocus
                            />
                            <Button size="sm" onClick={() => handleUpdatePrice(price.id)} className="h-8 bg-[#BFFF00] hover:bg-[#a6e600] text-black">
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditingPrice(null); setEditPriceValue(""); }} className="h-8">
                              <X size={14} />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-gray-700">฿{price.value}</span>
                            {price.isDefault ? (
                              <span className="text-xs text-gray-400 italic">ค่าเริ่มต้น</span>
                            ) : (
                              <>
                                <button
                                  onClick={() => { setEditingPrice(price.id); setEditPriceValue(price.value.toString()); }}
                                  className="p-1.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  onClick={() => handleDeletePrice(price.id)}
                                  className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>

                {/* Branches Tab */}
                <TabsContent value="branches" className="space-y-4 mt-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add new branch..."
                      value={newBranch}
                      onChange={(e) => setNewBranch(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddBranch()}
                      className="border-gray-200"
                      data-testid="new-branch-input"
                    />
                    <Button onClick={handleAddBranch} className="bg-[#BFFF00] hover:bg-[#a6e600] text-black">
                      <Plus size={18} />
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {branches.map((branch) => (
                      <div key={branch.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        {!branch.isDefault && editingBranch === branch.id ? (
                          <>
                            <Input
                              value={editBranchValue}
                              onChange={(e) => setEditBranchValue(e.target.value)}
                              className="flex-1 border-gray-200 h-8"
                              autoFocus
                            />
                            <Button size="sm" onClick={() => handleUpdateBranch(branch.id)} className="h-8 bg-[#BFFF00] hover:bg-[#a6e600] text-black">
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditingBranch(null); setEditBranchValue(""); }} className="h-8">
                              <X size={14} />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-gray-700">{branch.name}</span>
                            {branch.isDefault ? (
                              <span className="text-xs text-gray-400 italic">ค่าเริ่มต้น</span>
                            ) : (
                              <>
                                <button
                                  onClick={() => { setEditingBranch(branch.id); setEditBranchValue(branch.name); }}
                                  className="p-1.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  onClick={() => handleDeleteBranch(branch.id)}
                                  className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>

                {/* Banks Tab */}
                <TabsContent value="banks" className="space-y-4 mt-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add new bank..."
                      value={newBank}
                      onChange={(e) => setNewBank(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddBank()}
                      className="border-gray-200"
                    />
                    <Button onClick={handleAddBank} className="bg-[#BFFF00] hover:bg-[#a6e600] text-black">
                      <Plus size={18} />
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {banks.map((bank) => (
                      <div key={bank.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        {!bank.isDefault && editingBank === bank.id ? (
                          <>
                            <Input
                              value={editBankValue}
                              onChange={(e) => setEditBankValue(e.target.value)}
                              className="flex-1 border-gray-200 h-8"
                              autoFocus
                            />
                            <Button size="sm" onClick={() => handleUpdateBank(bank.id)} className="h-8 bg-[#BFFF00] hover:bg-[#a6e600] text-black">
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditingBank(null); setEditBankValue(""); }} className="h-8">
                              <X size={14} />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-gray-700">{bank.name}</span>
                            {bank.isDefault ? (
                              <span className="text-xs text-gray-400 italic">ค่าเริ่มต้น</span>
                            ) : (
                              <>
                                <button
                                  onClick={() => { setEditingBank(bank.id); setEditBankValue(bank.name); }}
                                  className="p-1.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  onClick={() => handleDeleteBank(bank.id)}
                                  className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>

          {/* Add Refund Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-refund-btn"
                className="bg-[#BFFF00] hover:bg-[#a6e600] text-black font-medium rounded-full"
                onClick={() => resetForm()}
              >
                <Plus size={18} className="sm:mr-2" />
                <span className="hidden sm:inline">Add Refund</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-gray-900">
                  {editingRefund ? "Edit Refund" : "Add New Refund"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Date</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="border-gray-200"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Price (฿)</Label>
                    <Select
                      value={formData.price}
                      onValueChange={(v) => setFormData({ ...formData, price: v })}
                    >
                      <SelectTrigger className="border-gray-200">
                        <SelectValue placeholder="Select price" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {prices.sort((a, b) => a.value - b.value).map((p) => (
                          <SelectItem key={p.id} value={p.value.toString()}>฿{p.value}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                      required
                      className="border-gray-200"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Total</Label>
                  <div className="p-3 bg-gray-100 rounded-lg text-lg font-semibold text-gray-900">
                    ฿{((parseFloat(formData.price) || 0) * (parseInt(formData.quantity) || 1)).toLocaleString()}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Branch</Label>
                  <Select
                    value={formData.branch}
                    onValueChange={(v) => setFormData({ ...formData, branch: v })}
                  >
                    <SelectTrigger className="border-gray-200">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger className="border-gray-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {statuses.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Bank Account Info (Optional) */}
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-400 mb-3">Bank Account Info (Optional)</p>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-gray-700">Account Number</Label>
                      <Input
                        value={formData.accountNumber}
                        onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                        placeholder="Enter account number"
                        className="border-gray-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Account Name</Label>
                      <Input
                        value={formData.accountName}
                        onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                        placeholder="Enter account name"
                        className="border-gray-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Bank</Label>
                      <Select
                        value={formData.bank}
                        onValueChange={(v) => setFormData({ ...formData, bank: v })}
                      >
                        <SelectTrigger className="border-gray-200">
                          <SelectValue placeholder="Select bank" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {banks.map((b) => (
                            <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Slip Upload */}
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-400 mb-3 flex items-center gap-1.5">
                    <ImageIcon size={13} />
                    สลิปการโอนเงินคืน
                    <span className="text-gray-300">(ถ้ามี)</span>
                  </p>
                  {(slipPreview || formData.slip_image_url) ? (
                    <div className="relative mb-3">
                      <img
                        src={slipPreview || formData.slip_image_url}
                        alt="สลิป"
                        className="w-full rounded-xl object-contain max-h-52 border border-gray-100 bg-gray-50"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setSlipFile(null);
                          setSlipPreview(null);
                          setFormData((f) => ({ ...f, slip_image_url: "" }));
                        }}
                        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-gray-900/60 text-white flex items-center justify-center hover:bg-gray-900 transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : null}
                  <label className="flex items-center justify-center gap-2 w-full py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-colors">
                    <Upload size={14} />
                    {slipFile ? slipFile.name : "คลิกเพื่อเลือกรูปสลิป"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setSlipFile(file);
                        setSlipPreview(URL.createObjectURL(file));
                      }}
                    />
                  </label>
                  {uploadingSlip && (
                    <div className="mt-2">
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#BFFF00] rounded-full transition-all duration-200"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1 text-right">{Math.round(uploadProgress)}%</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={resetForm} className="flex-1 border-gray-200">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={uploadingSlip} className="flex-1 bg-[#BFFF00] hover:bg-[#a6e600] text-black disabled:opacity-60">
                    {uploadingSlip ? "กำลังอัพโหลด..." : editingRefund ? "Update" : "Add Refund"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {format(currentMonth, "MMMM yyyy")}
        </h2>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={previousMonth} className="h-8 w-8 p-0 hover:bg-gray-100">
            <ChevronLeft size={18} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date())} className="h-8 px-3 hover:bg-gray-100 text-sm">
            Today
          </Button>
          <Button variant="ghost" size="sm" onClick={nextMonth} className="h-8 w-8 p-0 hover:bg-gray-100">
            <ChevronRight size={18} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Refund Table */}
        <div className="lg:col-span-3">
          <Card className="bg-white border-gray-100 shadow-sm rounded-2xl">
            <CardContent className="p-0">

              {/* ── Mobile card list (hidden on md+) ─────────────── */}
              <div className="md:hidden">
                {monthlyRefunds.length === 0 ? (
                  <p className="p-8 text-center text-gray-500 text-sm">No refunds for this month</p>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {monthlyRefunds.map((refund, idx) => (
                      <div key={refund.id} className="p-4 space-y-3">
                        {/* Row 1: index + date + branch + status */}
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 w-5">{idx + 1}</span>
                            <span className="text-sm font-semibold text-gray-800">
                              {format(parseISO(refund.date), "d MMM yyyy")}
                            </span>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getBranchColor(refund.branch)}`}>
                              {refund.branch}
                            </span>
                          </div>
                          <Select value={refund.status} onValueChange={(v) => handleStatusChange(refund, v)}>
                            <SelectTrigger className={`h-7 w-28 text-xs font-medium border-0 ${
                              refund.status === "Transfered" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                            }`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                              {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Row 2: account info + total + actions */}
                        <div className="flex items-end justify-between">
                          <div className="text-xs space-y-0.5 text-gray-500">
                            {refund.accountName && <div className="font-medium text-gray-700">{refund.accountName}</div>}
                            {refund.accountNumber && <div className="font-mono">{refund.accountNumber}</div>}
                            {refund.bank && <div>{refund.bank}</div>}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-base font-bold text-gray-900">
                              ฿{(Number(refund.total) || 0).toLocaleString()}
                            </span>
                            <div className="flex gap-1">
                              {refund.slip_image_url && (
                                <button onClick={() => setViewingSlip(refund.slip_image_url)}
                                  className="p-2 rounded-lg bg-gray-50 text-gray-400 hover:text-blue-500 active:bg-gray-100">
                                  <ImageIcon size={15} />
                                </button>
                              )}
                              <button onClick={() => handleEdit(refund)}
                                className="p-2 rounded-lg bg-gray-50 text-gray-400 hover:text-gray-700 active:bg-gray-100">
                                <Pencil size={15} />
                              </button>
                              <button onClick={() => handleDelete(refund.id)}
                                className="p-2 rounded-lg bg-gray-50 text-gray-400 hover:text-red-500 active:bg-red-50">
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Desktop table (hidden on mobile) ─────────────── */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full min-w-[820px]">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-3 py-4 text-xs font-medium text-gray-500 uppercase w-10">No.</th>
                      <th className="text-left px-3 py-4 text-xs font-medium text-gray-500 uppercase w-12">Date</th>
                      <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="text-left px-2 py-4 text-xs font-medium text-gray-500 uppercase w-10">QTY</th>
                      <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase min-w-[130px]">Branch</th>
                      <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase min-w-[150px]">Account Info</th>
                      <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="text-center p-4 text-xs font-medium text-gray-500 uppercase min-w-[120px]">Status</th>
                      <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase min-w-[80px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyRefunds.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="p-8 text-center text-gray-500">
                          No refunds for this month
                        </td>
                      </tr>
                    ) : (
                      monthlyRefunds.map((refund, idx) => (
                        <tr key={refund.id} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="px-3 py-4 text-gray-600">{idx + 1}</td>
                          <td className="px-3 py-4 text-gray-900">{format(parseISO(refund.date), "d")}</td>
                          <td className="p-4">
                            <span className="px-2 py-1 bg-gray-100 rounded text-gray-700">
                              ฿{refund.price}
                            </span>
                          </td>
                          <td className="px-2 py-4 text-gray-600 text-center">{refund.quantity}</td>
                          <td className="p-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${getBranchColor(refund.branch)}`}>
                              {refund.branch}
                            </span>
                          </td>
                          <td className="p-4">
                            {refund.accountNumber || refund.accountName || refund.bank ? (
                              <div className="text-xs space-y-0.5">
                                {refund.accountName && (
                                  <div className="font-medium text-gray-900">{refund.accountName}</div>
                                )}
                                {refund.accountNumber && (
                                  <div className="text-gray-600 font-mono">{refund.accountNumber}</div>
                                )}
                                {refund.bank && (
                                  <div className="text-gray-500">{refund.bank}</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="p-4 text-right font-medium text-gray-900">฿{(Number(refund.total) || 0).toLocaleString()}</td>
                          <td className="p-4 text-center">
                            <Select
                              value={refund.status}
                              onValueChange={(v) => handleStatusChange(refund, v)}
                            >
                              <SelectTrigger className={`h-8 w-28 text-xs font-medium border-0 ${
                                refund.status === "Transfered"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-amber-50 text-amber-700"
                              }`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-gray-200">
                                {statuses.map((s) => (
                                  <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex justify-end gap-1">
                              {refund.slip_image_url && (
                                <button
                                  onClick={() => setViewingSlip(refund.slip_image_url)}
                                  className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-500"
                                  title="ดูสลิป"
                                >
                                  <ImageIcon size={14} />
                                </button>
                              )}
                              <button
                                onClick={() => handleEdit(refund)}
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                onClick={() => handleDelete(refund.id)}
                                className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* end desktop table */}

            </CardContent>
          </Card>
        </div>

        {/* Summary Sidebar */}
        <div className="space-y-4">
          {/* Total Card */}
          <Card className="bg-[#1a1a1a] border-0 rounded-2xl">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">Total Refund</p>
              <p className="text-3xl font-bold text-[#BFFF00]">฿{(totalMonthlyRefund || 0).toLocaleString()}</p>
              <p className="text-sm text-gray-400 mt-1">{monthlyRefunds.length} transactions</p>
            </CardContent>
          </Card>

          {/* Branch Summary */}
          <Card className="bg-white border-gray-100 shadow-sm rounded-2xl">
            <CardHeader className="pb-2 border-b border-gray-100">
              <CardTitle className="text-base font-semibold text-gray-900">By Branch</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {Object.keys(branchSummary).length === 0 ? (
                <div className="p-4 text-center text-gray-400 text-sm">
                  No data for this month
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {Object.entries(branchSummary)
                    .sort((a, b) => b[1].total - a[1].total)
                    .map(([branch, data]) => (
                      <div key={branch} className="p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getBranchColor(branch)}`}>
                            {branch}
                          </span>
                          <span className="text-xs text-gray-400">×{data.count}</span>
                        </div>
                        <span className="font-semibold text-gray-900">฿{(Number(data.total) || 0).toLocaleString()}</span>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Slip Lightbox */}
      {viewingSlip && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setViewingSlip(null)}
        >
          <div
            className="relative max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setViewingSlip(null)}
              className="absolute -top-10 right-0 text-white/70 hover:text-white flex items-center gap-1.5 text-sm transition-colors"
            >
              <X size={16} /> ปิด
            </button>
            <img
              src={viewingSlip}
              alt="สลิปการโอนเงินคืน"
              className="w-full rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundPage;
